// =============================================================================
// Portal Wallet Page — balance, transactions, top-up, auto-recharge settings
// -----------------------------------------------------------------------------
// Reads `GET /api/v1/wallet` and `GET /api/v1/wallet/transactions` for state,
// posts `POST /api/v1/wallet/top-up-session` for the Stripe Checkout redirect,
// and patches `PATCH /api/v1/wallet/settings` to update the auto-recharge
// configuration. All amounts travel as integer-cent strings (`MoneyDto`) and
// are formatted on the client with `formatMoneyDto` from `@tkpod/shared`.
//
// The top-up flow uses Stripe Checkout (server creates the session, client
// redirects). Returning to the portal is handled via the success/cancel
// query parameters wired here so we can show a confirmation banner without
// needing a websocket.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import {
  formatMoneyDto,
  type MoneyDto,
  type UpdateAutoRechargeRequest,
  type WalletBalanceDto,
  type WalletTransactionDto,
  type WalletTransactionListResponse,
  type WalletStoreSpendDto,
  type CreateTopUpSessionResponse,
} from '@tkpod/shared';
import { Button, Card, CardHeader, Modal, ModalFooter, Spinner } from '@tkpod/ui';
import { StoreFilter, NO_STORE } from '../components/StoreFilter';
import { useConnections } from '../hooks/use-connections';

// ---------------------------------------------------------------------------
// Constants — quick top-up presets keep the average flow under 3 clicks.
// Operator-configurable bounds (env-driven) cap what the API will accept;
// the presets here just steer the merchant at the common cases.
// ---------------------------------------------------------------------------

const TOP_UP_PRESETS_CENTS = [5_000n, 10_000n, 25_000n, 50_000n, 100_000n];
const MIN_TOP_UP_CENTS = 100n; // $1 — Stripe minimum is $0.50 worldwide
const MAX_TOP_UP_CENTS = 5_000_000n; // $50,000 — DTO upper bound

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dollarsStringToCents(input: string): bigint | null {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, frac = ''] = trimmed.split('.');
  const padded = frac.padEnd(2, '0');
  try {
    return BigInt(whole) * 100n + BigInt(padded);
  } catch {
    return null;
  }
}

function centsToDollarsInput(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const frac = abs % 100n;
  return `${negative ? '-' : ''}${whole.toString()}.${frac
    .toString()
    .padStart(2, '0')}`;
}

function reasonLabel(reason: WalletTransactionDto['reason']): string {
  switch (reason) {
    case 'TOP_UP':
      return 'Top-up';
    case 'AUTO_RECHARGE':
      return 'Auto-recharge';
    case 'ORDER_FULFILLMENT':
      return 'Order charge';
    case 'ORDER_REFUND':
      return 'Order refund';
    case 'CHARGEBACK':
      return 'Chargeback';
    case 'CHARGEBACK_REVERSAL':
      return 'Chargeback reversal';
    case 'MANUAL_ADJUSTMENT':
      return 'Manual adjustment';
    case 'PROMOTIONAL_CREDIT':
      return 'Promotional credit';
    case 'HOLD_PLACED':
      return 'Hold placed';
    case 'HOLD_RELEASED':
      return 'Hold released';
    default:
      return reason ?? 'Movement';
  }
}

function isCredit(amount: MoneyDto): boolean {
  return !amount.amountCents.startsWith('-');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PortalWalletPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Banner state driven by ?topup=success|cancelled query params (set by the
  // success/cancel URLs we hand to Stripe Checkout). We strip the query on
  // mount so a refresh doesn't re-trigger the banner.
  const [banner, setBanner] = useState<
    { tone: 'success' | 'info' | 'error'; message: string } | null
  >(null);

  useEffect(() => {
    const status = searchParams.get('topup');
    if (status === 'success') {
      setBanner({
        tone: 'success',
        message:
          'Top-up received — funds appear here within a few seconds of Stripe confirming the charge.',
      });
    } else if (status === 'cancelled') {
      setBanner({
        tone: 'info',
        message: 'Top-up was cancelled. No charge was made.',
      });
    }
    if (status) {
      const next = new URLSearchParams(searchParams);
      next.delete('topup');
      setSearchParams(next, { replace: true });
    }
  }, []);

  // Store-scoped view filter for the transaction list. Wallet *balance*
  // itself stays org-level (shared) — only the activity list narrows.
  // URL param name (`connectionId`) is shared with Orders/Products/Payments
  // so a future global store switcher can drive every page uniformly.
  const connectionId = searchParams.get('connectionId') ?? '';

  const connectionsQuery = useConnections();
  const storeName = useMemo(() => {
    if (connectionId === NO_STORE) return 'manual (no store) movements';
    return (
      connectionsQuery.data?.find((c) => c.id === connectionId)?.name ??
      'the selected store'
    );
  }, [connectionId, connectionsQuery.data]);

  const setStore = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('connectionId', value);
    else next.delete('connectionId');
    setSearchParams(next);
  };

  const balanceQuery = useQuery<WalletBalanceDto>({
    queryKey: ['wallet', 'balance'],
    queryFn: () => api.get<WalletBalanceDto>('/api/v1/wallet'),
    refetchInterval: 30_000,
  });

  const txQuery = useQuery<WalletTransactionListResponse>({
    queryKey: ['wallet', 'transactions', { connectionId }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', '25');
      if (connectionId) params.set('connectionId', connectionId);
      return api.get<WalletTransactionListResponse>(
        `/api/v1/wallet/transactions?${params.toString()}`,
      );
    },
  });

  const storeSpendQuery = useQuery<WalletStoreSpendDto[]>({
    queryKey: ['wallet', 'by-store'],
    queryFn: () => api.get<WalletStoreSpendDto[]>('/api/v1/wallet/by-store'),
    refetchInterval: 60_000,
  });

  const topUpMutation = useMutation({
    mutationFn: async (amountCents: bigint) => {
      const origin = window.location.origin;
      const successUrl = `${origin}/billing?tab=wallet&topup=success`;
      const cancelUrl = `${origin}/billing?tab=wallet&topup=cancelled`;
      return api.post<CreateTopUpSessionResponse>(
        '/api/v1/wallet/top-up-session',
        {
          amountCents: amountCents.toString(),
          successUrl,
          cancelUrl,
        },
      );
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (body: UpdateAutoRechargeRequest) =>
      api.patch<WalletBalanceDto>('/api/v1/wallet/settings', body),
    onSuccess: (data) => {
      queryClient.setQueryData(['wallet', 'balance'], data);
      setSettingsOpen(false);
      setBanner({
        tone: 'success',
        message: 'Auto-recharge settings saved.',
      });
    },
  });

  const balance = balanceQuery.data;

  // Lookup so the transaction list can render a store name from the
  // denormalized `connectionId` without an extra round-trip per row.
  const storeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of connectionsQuery.data ?? []) map.set(c.id, c.name);
    return map;
  }, [connectionsQuery.data]);

  const storeSpend = storeSpendQuery.data ?? [];

  return (
    <div className="space-y-6">
      {banner && (
        <div
          role="status"
          className={
            banner.tone === 'success'
              ? 'rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100'
              : banner.tone === 'error'
                ? 'rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100'
                : 'rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100'
          }
        >
          <div className="flex items-start justify-between gap-3">
            <span>{banner.message}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="text-xs font-medium underline opacity-80 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Balance summary */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Wallet balance" />
          <div className="space-y-4 px-4 pb-5 sm:px-5">
            {balanceQuery.isLoading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner />
              </div>
            ) : balanceQuery.isError || !balance ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600">
                  {balanceQuery.error instanceof Error
                    ? balanceQuery.error.message
                    : 'Unable to load wallet balance.'}
                </p>
                <Button
                  variant="secondary"
                  onClick={() => balanceQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <BalanceTile
                    label="Available"
                    money={balance.availableBalance}
                    emphasis
                  />
                  <BalanceTile label="Total" money={balance.balance} />
                  <BalanceTile label="On hold" money={balance.heldAmount} />
                </div>
                {balance.isFrozen && (
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
                    Wallet is frozen
                    {balance.freezeReason ? `: ${balance.freezeReason}` : '.'}{' '}
                    Contact support to restore activity.
                  </div>
                )}
                {!balance.isFrozen &&
                  balance.lowBalanceThreshold &&
                  BigInt(balance.availableBalance.amountCents) <
                    BigInt(balance.lowBalanceThreshold.amountCents) && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                      Low balance — available drops below your warning
                      threshold ({formatMoneyDto(balance.lowBalanceThreshold)}).
                      Top up or enable auto-recharge to keep fulfillment
                      uninterrupted.
                    </div>
                  )}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => setTopUpOpen(true)}
                    disabled={balance.isFrozen}
                  >
                    Top up wallet
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setSettingsOpen(true)}
                    disabled={balance.isFrozen}
                  >
                    Auto-recharge settings
                  </Button>
                </div>
                <p className="text-xs text-text-tertiary">
                  This wallet is shared across every store in your account.
                  Order charges from all connected stores draw from the same
                  balance.
                </p>
              </>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Auto-recharge" />
          <div className="space-y-3 px-4 pb-5 text-sm sm:px-5">
            {balance ? (
              balance.autoRecharge ? (
                <>
                  <p className="text-text-primary">
                    Enabled — when your available balance drops below{' '}
                    <span className="font-medium">
                      {balance.rechargeThreshold
                        ? formatMoneyDto(balance.rechargeThreshold)
                        : '—'}
                    </span>
                    , we'll top up{' '}
                    <span className="font-medium">
                      {balance.rechargeAmount
                        ? formatMoneyDto(balance.rechargeAmount)
                        : '—'}
                    </span>{' '}
                    using your default payment method.
                  </p>
                  <p className="text-text-secondary text-xs">
                    Make sure a default card is set on the{' '}
                    <Link
                      to="/billing?tab=payment-methods"
                      className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      Payment methods
                    </Link>{' '}
                    page — auto-recharge will fail without one.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-text-primary">
                    Disabled. Orders won't be charged until you top up
                    manually.
                  </p>
                  <p className="text-text-secondary text-xs">
                    Enable auto-recharge to keep fulfillment uninterrupted.
                  </p>
                </>
              )
            ) : (
              <p className="text-text-secondary">Loading…</p>
            )}
          </div>
        </Card>
      </div>

      {/* Spend by store — net wholesale charged to the shared wallet,
          attributed to each connected store via the denormalized
          WalletTransaction.connectionId. Top-ups/refunds without a store
          land in the "Manual / Unassigned" bucket. */}
      {storeSpend.length > 0 && (
        <Card padding="none">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-lg font-semibold text-text-primary">
              Spend by store
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              Net wholesale charged to your shared wallet, broken down by the
              store that produced each order.
            </p>
          </div>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Store</th>
                <th className="px-5 py-2 text-right font-semibold">Orders</th>
                <th className="px-5 py-2 text-right font-semibold">
                  Net spent
                </th>
              </tr>
            </thead>
            <tbody>
              {storeSpend.map((row) => (
                <tr
                  key={row.connectionId ?? 'manual'}
                  className="border-b border-border/60 last:border-b-0"
                >
                  <td className="px-5 py-3 text-text-primary">
                    {row.storeName}
                  </td>
                  <td className="px-5 py-3 text-right text-text-secondary">
                    {row.orderCount}
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-text-primary">
                    {formatMoneyDto(row.netSpent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Transactions */}
      <Card padding="none">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              Recent activity
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              Most recent 25 movements (top-ups, order charges, refunds, and
              manual adjustments)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-[180px]">
              <StoreFilter
                value={connectionId}
                onChange={setStore}
                includeManual
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => txQuery.refetch()}
              disabled={txQuery.isFetching}
            >
              Refresh
            </Button>
          </div>
        </div>
        {connectionId && (
          <div className="border-b border-border bg-surface-secondary/30 px-5 py-2 text-xs text-text-tertiary">
            Showing activity for{' '}
            <span className="font-medium text-text-secondary">{storeName}</span>
            . The wallet balance above is shared across all stores.
          </div>
        )}
        {txQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : txQuery.isError ? (
          <div className="space-y-2 px-5 py-6">
            <p className="text-sm text-rose-600">
              {txQuery.error instanceof Error
                ? txQuery.error.message
                : 'Unable to load wallet activity.'}
            </p>
            <Button variant="secondary" onClick={() => txQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : !txQuery.data?.items.length ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm text-text-secondary">
              No wallet activity yet.
            </p>
            <p className="mt-1 text-xs text-text-tertiary">
              Top up to see your first credit row appear here.
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Date</th>
                <th className="px-5 py-2 font-semibold">Reason</th>
                <th className="hidden px-5 py-2 font-semibold md:table-cell">
                  Store
                </th>
                <th className="px-5 py-2 font-semibold">Description</th>
                <th className="px-5 py-2 text-right font-semibold">Amount</th>
                <th className="px-5 py-2 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody>
              {txQuery.data.items.map((tx) => (
                <tr
                  key={tx.id}
                  className="border-b border-border/60 last:border-b-0"
                >
                  <td className="px-5 py-3 align-top text-text-secondary">
                    {new Date(tx.createdAt).toLocaleString()}
                  </td>
                  <td className="px-5 py-3 align-top text-text-primary">
                    {reasonLabel(tx.reason)}
                  </td>
                  <td className="hidden px-5 py-3 align-top text-text-secondary md:table-cell">
                    {tx.connectionId ? (
                      (storeNameById.get(tx.connectionId) ?? 'Store')
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 align-top text-text-secondary">
                    {tx.description ?? '—'}
                  </td>
                  <td
                    className={`px-5 py-3 text-right align-top font-medium ${
                      isCredit(tx.amount)
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-rose-600 dark:text-rose-400'
                    }`}
                  >
                    {formatMoneyDto(tx.amount, { sign: 'always' })}
                  </td>
                  <td className="px-5 py-3 text-right align-top text-text-primary">
                    {formatMoneyDto(tx.balanceAfter)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Modals */}
      <TopUpModal
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        onSubmit={(cents) => topUpMutation.mutate(cents)}
        loading={topUpMutation.isPending}
        error={
          topUpMutation.error instanceof Error
            ? topUpMutation.error.message
            : null
        }
      />
      <AutoRechargeModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={(payload) => settingsMutation.mutate(payload)}
        loading={settingsMutation.isPending}
        error={
          settingsMutation.error instanceof Error
            ? settingsMutation.error.message
            : null
        }
        initial={balance ?? null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BalanceTile({
  label,
  money,
  emphasis,
}: {
  label: string;
  money: MoneyDto;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface-tertiary px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </p>
      <p
        className={`mt-1 ${
          emphasis
            ? 'text-2xl font-semibold text-text-primary'
            : 'text-lg font-medium text-text-primary'
        }`}
      >
        {formatMoneyDto(money)}
      </p>
    </div>
  );
}

function TopUpModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (amountCents: bigint) => void;
  loading: boolean;
  error: string | null;
}) {
  const [selectedPreset, setSelectedPreset] = useState<bigint | null>(
    TOP_UP_PRESETS_CENTS[1] ?? null,
  );
  const [customInput, setCustomInput] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const customCents = useMemo(() => {
    if (!customInput.trim()) return null;
    return dollarsStringToCents(customInput);
  }, [customInput]);

  const effectiveCents = customCents ?? selectedPreset;

  function handleSubmit() {
    setValidationError(null);
    if (effectiveCents === null) {
      setValidationError('Choose a preset or enter a custom amount.');
      return;
    }
    if (effectiveCents < MIN_TOP_UP_CENTS) {
      setValidationError(
        `Minimum top-up is ${formatMoneyDto({ amountCents: MIN_TOP_UP_CENTS.toString(), currency: 'USD' })}.`,
      );
      return;
    }
    if (effectiveCents > MAX_TOP_UP_CENTS) {
      setValidationError(
        `Maximum single top-up is ${formatMoneyDto({ amountCents: MAX_TOP_UP_CENTS.toString(), currency: 'USD' })}.`,
      );
      return;
    }
    onSubmit(effectiveCents);
  }

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Top up wallet"
      description="Add funds via Stripe Checkout. You'll be redirected to Stripe to confirm — funds appear here within a few seconds of confirmation."
      size="md"
    >
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
            Quick amounts
          </p>
          <div className="grid grid-cols-3 gap-2">
            {TOP_UP_PRESETS_CENTS.map((cents) => {
              const selected =
                customCents === null && selectedPreset === cents;
              return (
                <button
                  key={cents.toString()}
                  type="button"
                  onClick={() => {
                    setSelectedPreset(cents);
                    setCustomInput('');
                    setValidationError(null);
                  }}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                    selected
                      ? 'border-brand-600 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-200'
                      : 'border-border text-text-primary hover:bg-surface-tertiary'
                  }`}
                  disabled={loading}
                >
                  {formatMoneyDto({
                    amountCents: cents.toString(),
                    currency: 'USD',
                  })}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label
            htmlFor="topup-custom"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Or custom amount (USD)
          </label>
          <input
            id="topup-custom"
            type="text"
            inputMode="decimal"
            value={customInput}
            onChange={(e) => {
              setCustomInput(e.target.value);
              setValidationError(null);
            }}
            placeholder="100.00"
            disabled={loading}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-600 focus:outline-none"
          />
        </div>

        {(validationError || error) && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
            {validationError ?? error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading
            ? 'Redirecting…'
            : `Continue${
                effectiveCents
                  ? ' • ' +
                    formatMoneyDto({
                      amountCents: effectiveCents.toString(),
                      currency: 'USD',
                    })
                  : ''
              }`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function AutoRechargeModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: UpdateAutoRechargeRequest) => void;
  loading: boolean;
  error: string | null;
  initial: WalletBalanceDto | null;
}) {
  const [enabled, setEnabled] = useState<boolean>(initial?.autoRecharge ?? false);
  const [amountInput, setAmountInput] = useState<string>(
    initial?.rechargeAmount
      ? centsToDollarsInput(BigInt(initial.rechargeAmount.amountCents))
      : '50.00',
  );
  const [thresholdInput, setThresholdInput] = useState<string>(
    initial?.rechargeThreshold
      ? centsToDollarsInput(BigInt(initial.rechargeThreshold.amountCents))
      : '20.00',
  );
  const [warnInput, setWarnInput] = useState<string>(
    initial?.lowBalanceThreshold
      ? centsToDollarsInput(BigInt(initial.lowBalanceThreshold.amountCents))
      : '',
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Re-sync from server data whenever the modal re-opens against a fresh
  // balance snapshot. Guard against the server sending null after a
  // disable so the inputs don't blank out unexpectedly.
  useEffect(() => {
    if (!open || !initial) return;
    setEnabled(initial.autoRecharge);
    if (initial.rechargeAmount) {
      setAmountInput(centsToDollarsInput(BigInt(initial.rechargeAmount.amountCents)));
    }
    if (initial.rechargeThreshold) {
      setThresholdInput(
        centsToDollarsInput(BigInt(initial.rechargeThreshold.amountCents)),
      );
    }
    setWarnInput(
      initial.lowBalanceThreshold
        ? centsToDollarsInput(BigInt(initial.lowBalanceThreshold.amountCents))
        : '',
    );
    setValidationError(null);
  }, [open, initial]);

  function handleSubmit() {
    setValidationError(null);
    if (enabled) {
      const amountCents = dollarsStringToCents(amountInput);
      const thresholdCents = dollarsStringToCents(thresholdInput);
      if (amountCents === null) {
        setValidationError('Recharge amount must be a valid USD value.');
        return;
      }
      if (thresholdCents === null) {
        setValidationError('Recharge threshold must be a valid USD value.');
        return;
      }
      const warnCents = warnInput.trim()
        ? dollarsStringToCents(warnInput)
        : null;
      if (warnInput.trim() && warnCents === null) {
        setValidationError('Low-balance warning must be a valid USD value.');
        return;
      }
      onSubmit({
        autoRecharge: true,
        rechargeAmountCents: amountCents.toString(),
        rechargeThresholdCents: thresholdCents.toString(),
        lowBalanceThresholdCents: warnCents?.toString(),
      });
    } else {
      const warnCents = warnInput.trim()
        ? dollarsStringToCents(warnInput)
        : null;
      if (warnInput.trim() && warnCents === null) {
        setValidationError('Low-balance warning must be a valid USD value.');
        return;
      }
      onSubmit({
        autoRecharge: false,
        lowBalanceThresholdCents: warnCents?.toString(),
      });
    }
  }

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Auto-recharge settings"
      description="Keep the wallet topped up automatically by charging your default payment method when the available balance drops below a threshold you choose."
      size="md"
    >
      <div className="space-y-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              setValidationError(null);
            }}
            disabled={loading}
            className="mt-1 h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-600"
          />
          <span>
            <span className="block text-sm font-medium text-text-primary">
              Enable auto-recharge
            </span>
            <span className="block text-xs text-text-secondary">
              When unchecked, you'll need to top up manually whenever the
              balance runs low.
            </span>
          </span>
        </label>

        {enabled && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="auto-amount"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
              >
                Recharge amount (USD)
              </label>
              <input
                id="auto-amount"
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  setValidationError(null);
                }}
                disabled={loading}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-600 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="auto-threshold"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
              >
                Trigger when below (USD)
              </label>
              <input
                id="auto-threshold"
                type="text"
                inputMode="decimal"
                value={thresholdInput}
                onChange={(e) => {
                  setThresholdInput(e.target.value);
                  setValidationError(null);
                }}
                disabled={loading}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-600 focus:outline-none"
              />
            </div>
          </div>
        )}

        <div>
          <label
            htmlFor="auto-warn"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Low-balance warning (USD, optional)
          </label>
          <input
            id="auto-warn"
            type="text"
            inputMode="decimal"
            value={warnInput}
            onChange={(e) => {
              setWarnInput(e.target.value);
              setValidationError(null);
            }}
            placeholder="Leave empty to disable the warning banner"
            disabled={loading}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-600 focus:outline-none"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            UI-only banner — does not trigger a charge.
          </p>
        </div>

        {(validationError || error) && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
            {validationError ?? error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving…' : 'Save settings'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
